import {
  createSealedVisualizationProof,
  EMPTY_VISUALIZATION_OUTBOX_STATE,
  isVisualizationRecordEligibleForInstance,
  SealedVisualizationProof,
  VisualizationCounter,
  VisualizationProofOutboxState,
  VisualizationProofStore,
} from './visualization-proof-outbox.types';

interface PersistedVisualizationProofOutboxState extends VisualizationProofOutboxState {
  version: 2;
}

export const VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY = 'visualization-proof-outbox:v2';

interface StorageLockManager {
  request<T>(name: string, callback: () => T | PromiseLike<T>): Promise<T>;
}

export class LocalStorageVisualizationProofStore implements VisualizationProofStore {
  readonly kind = 'local-storage' as const;
  private operationQueue: Promise<void> = Promise.resolve();

  static openShared(storage: Storage): LocalStorageVisualizationProofStore | null {
    const lockManager = getStorageLockManager();
    return lockManager ? new LocalStorageVisualizationProofStore(storage, lockManager) : null;
  }

  constructor(
    private readonly storage: Storage,
    private readonly lockManager: StorageLockManager,
  ) {}

  probe(): void {
    const key = `${VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY}:probe`;
    try {
      this.storage.setItem(key, '1');
      this.storage.removeItem(key);
    } catch (error) {
      // A full store can still drain and delete an existing durable outbox.
      if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') {
        throw error;
      }
    }
  }

  incrementCounter(counter: VisualizationCounter): Promise<void> {
    return this.runExclusive(() => {
      const state = this.readState();
      const index = state.counters.findIndex((item) => item.key === counter.key);

      if (index === -1) {
        state.counters.push({ ...counter });
      } else {
        const current = state.counters[index];
        state.counters[index] = {
          ...current,
          identifier: counter.identifier,
          visualizations: current.visualizations + counter.visualizations,
          updatedAt: counter.updatedAt,
        };
      }

      this.writeState(state);
    });
  }

  importState(imported: VisualizationProofOutboxState): Promise<void> {
    return this.runExclusive(() => {
      const state = this.readState();
      const proofIds = new Set(state.proofs.map((proof) => proof.proofId));

      for (const proof of imported.proofs) {
        if (!proofIds.has(proof.proofId)) {
          state.proofs.push({ ...proof });
          proofIds.add(proof.proofId);
        }
      }

      for (const counter of imported.counters) {
        const index = state.counters.findIndex((item) => item.key === counter.key);

        if (index === -1) {
          state.counters.push({ ...counter });
        } else {
          state.counters[index] = {
            ...state.counters[index],
            visualizations: state.counters[index].visualizations + counter.visualizations,
            updatedAt: Math.max(state.counters[index].updatedAt, counter.updatedAt),
          };
        }
      }

      this.writeState(state);
    });
  }

  exportState(): Promise<VisualizationProofOutboxState> {
    return this.runExclusive(() => this.readState());
  }

  sealCounters(limit: number, instanceId?: string): Promise<void> {
    return this.runExclusive(() => {
      const state = this.readState();
      const sealLimit = Math.max(0, Math.floor(limit));
      let sealedCounters = 0;
      const remainingCounters: VisualizationCounter[] = [];
      const proofs: SealedVisualizationProof[] = [];

      for (const counter of state.counters) {
        if (
          sealedCounters >= sealLimit ||
          !isVisualizationRecordEligibleForInstance(counter, instanceId)
        ) {
          remainingCounters.push(counter);
          continue;
        }

        proofs.push(createSealedVisualizationProof(counter));
        sealedCounters += 1;
      }

      if (proofs.length === 0) {
        return;
      }

      state.counters = remainingCounters;
      state.proofs.push(...proofs);
      this.writeState(state);
    });
  }

  listProofs(limit: number, instanceId?: string): Promise<SealedVisualizationProof[]> {
    return this.runExclusive(() =>
      this.readState()
        .proofs.filter((proof) => isVisualizationRecordEligibleForInstance(proof, instanceId))
        .slice(0, Math.max(0, limit)),
    );
  }

  deleteProofs(proofIds: string[]): Promise<void> {
    if (proofIds.length === 0) {
      return Promise.resolve();
    }

    return this.runExclusive(() => {
      const proofIdSet = new Set(proofIds);
      const state = this.readState();
      state.proofs = state.proofs.filter((proof) => !proofIdSet.has(proof.proofId));
      this.writeState(state);
    });
  }

  clear(): Promise<void> {
    return this.runExclusive(() => this.storage.removeItem(VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY));
  }

  close(): void {}

  private runExclusive<T>(operation: () => T | PromiseLike<T>): Promise<T> {
    const result = this.operationQueue.then(() =>
      this.lockManager.request(`${VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY}:lock`, operation),
    );
    this.operationQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return Promise.resolve(result);
  }

  private readState(): VisualizationProofOutboxState {
    return readPersistedState(this.storage);
  }

  private writeState(state: VisualizationProofOutboxState): void {
    if (state.counters.length === 0 && state.proofs.length === 0) {
      this.storage.removeItem(VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY);
      return;
    }

    const persisted: PersistedVisualizationProofOutboxState = {
      version: 2,
      counters: state.counters,
      proofs: state.proofs,
    };

    this.storage.setItem(VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY, JSON.stringify(persisted));
  }
}

function readPersistedState(storage: Storage): VisualizationProofOutboxState {
  const rawValue = storage.getItem(VISUALIZATION_PROOF_OUTBOX_STORAGE_KEY);

  if (!rawValue) {
    return structuredCloneOutboxState(EMPTY_VISUALIZATION_OUTBOX_STATE);
  }

  const parsedValue: unknown = JSON.parse(rawValue);

  if (!isPersistedOutboxState(parsedValue)) {
    return structuredCloneOutboxState(EMPTY_VISUALIZATION_OUTBOX_STATE);
  }

  return {
    counters: parsedValue.counters
      .filter(isVisualizationCounter)
      .map((counter) => ({ ...counter })),
    proofs: parsedValue.proofs.filter(isSealedVisualizationProof).map((proof) => ({ ...proof })),
  };
}

function getStorageLockManager(): StorageLockManager | null {
  try {
    const locks = globalThis.navigator?.locks;

    if (!locks) {
      return null;
    }

    return {
      request: (name, callback) => locks.request(name, () => callback()),
    };
  } catch {
    return null;
  }
}

function isPersistedOutboxState(value: unknown): value is PersistedVisualizationProofOutboxState {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const state = value as Partial<PersistedVisualizationProofOutboxState>;

  return state.version === 2 && Array.isArray(state.counters) && Array.isArray(state.proofs);
}

function structuredCloneOutboxState(
  state: VisualizationProofOutboxState,
): VisualizationProofOutboxState {
  return {
    counters: state.counters.map((counter) => ({ ...counter })),
    proofs: state.proofs.map((proof) => ({ ...proof })),
  };
}

function isVisualizationCounter(value: unknown): value is VisualizationCounter {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const counter = value as Partial<VisualizationCounter>;
  const hasIdentity =
    ((counter.kind === undefined || counter.kind === 'campaign') &&
      typeof counter.mediaCampaignId === 'string') ||
    (counter.kind === 'programmatic' &&
      typeof counter.programmaticDeliveryId === 'string' &&
      typeof counter.authorizationId === 'string' &&
      typeof counter.instanceId === 'string' &&
      typeof counter.occurrenceId === 'string' &&
      counter.playerProtocolVersion === 2);

  return (
    typeof counter.key === 'string' &&
    hasIdentity &&
    typeof counter.mediaId === 'string' &&
    typeof counter.identifier === 'string' &&
    typeof counter.date === 'string' &&
    typeof counter.visualizations === 'number' &&
    Number.isSafeInteger(counter.visualizations) &&
    counter.visualizations > 0 &&
    typeof counter.updatedAt === 'number'
  );
}

function isSealedVisualizationProof(value: unknown): value is SealedVisualizationProof {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const proof = value as Partial<SealedVisualizationProof>;
  const hasIdentity =
    ((proof.kind === undefined || proof.kind === 'campaign') &&
      typeof proof.mediaCampaignId === 'string') ||
    (proof.kind === 'programmatic' &&
      typeof proof.programmaticDeliveryId === 'string' &&
      typeof proof.authorizationId === 'string' &&
      typeof proof.instanceId === 'string' &&
      typeof proof.occurrenceId === 'string' &&
      proof.playerProtocolVersion === 2);

  return (
    typeof proof.proofId === 'string' &&
    hasIdentity &&
    typeof proof.mediaId === 'string' &&
    typeof proof.identifier === 'string' &&
    typeof proof.date === 'string' &&
    typeof proof.visualizations === 'number' &&
    Number.isFinite(proof.visualizations) &&
    proof.visualizations > 0 &&
    typeof proof.createdAt === 'number'
  );
}
